import { Box, Typography, Card, CardContent, Grid, Chip, Button, CircularProgress, Drawer, IconButton } from '@mui/material';
import { X } from 'lucide-react';
import { DataGrid } from '@mui/x-data-grid';
import type { GridColDef, GridRowParams } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import { useTenants } from '@lingaai/shared-hooks';
import { useState } from 'react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

export function TenantList() {
  const { data: tenants, isLoading } = useTenants();
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const [costPeriod, setCostPeriod] = useState<'30d' | '90d' | '1y' | 'all'>('30d');

  // Fetch tenant costs
  const { data: tenantCosts } = useQuery({
    queryKey: ['tenant-costs'],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/admin/tenant-costs`);
      if (!response.ok) throw new Error('Failed to fetch tenant costs');
      return response.json();
    },
    refetchInterval: 60000,
  });

  // Fetch system health
  const { data: health, dataUpdatedAt: healthUpdatedAt } = useQuery({
    queryKey: ['admin-health'],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/admin/health`);
      if (!response.ok) throw new Error('Failed to fetch health');
      return response.json();
    },
    refetchInterval: 30000,
  });

  // Fetch metrics with period filter
  const { data: metrics } = useQuery({
    queryKey: ['admin-metrics', costPeriod],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/admin/metrics?period=${costPeriod}`);
      if (!response.ok) throw new Error('Failed to fetch metrics');
      return response.json();
    },
    refetchInterval: 60000,
  });

  // Fetch platform links
  const { data: links } = useQuery({
    queryKey: ['admin-links'],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/admin/links`);
      if (!response.ok) throw new Error('Failed to fetch links');
      return response.json();
    },
  });

  // Fetch tenant details when drawer opens
  const { data: tenantDetails } = useQuery({
    queryKey: ['tenant-details', selectedTenantId],
    queryFn: async () => {
      if (!selectedTenantId) return null;
      const response = await fetch(`${API_URL}/admin/tenant-details/${selectedTenantId}`);
      if (!response.ok) throw new Error('Failed to fetch tenant details');
      return response.json();
    },
    enabled: !!selectedTenantId,
  });

  const getStatusColor = (status: string) => {
    if (status === 'ok' || status === 'healthy') return 'success';
    if (status === 'error') return 'error';
    return 'warning';
  };

  const formatLastUpdate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  // Merge tenant data with cost data
  const tenantsWithCosts = tenants?.map((tenant) => {
    const costData = tenantCosts?.find((c: any) => c.tenant_id === tenant.id);
    return {
      ...tenant,
      total_runs: costData?.total_runs || 0,
      total_cost: costData?.total_cost || 0,
      avg_cost_per_run: costData?.avg_cost_per_run || 0,
    };
  });

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Tenant Name',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'workspace_slug',
      headerName: 'Workspace Slug',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'total_runs',
      headerName: 'Runs (30d)',
      width: 120,
      type: 'number',
    },
    {
      field: 'total_cost',
      headerName: 'Cost (30d)',
      width: 120,
      valueFormatter: (value) => `$${Number(value).toFixed(2)}`,
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 120,
      valueFormatter: (value) => {
        return new Date(value).toLocaleDateString();
      },
    },
  ];

  const handleRowClick = (params: GridRowParams) => {
    setSelectedTenantId(params.row.id);
  };

  const selectedTenant = tenantsWithCosts?.find((t) => t.id === selectedTenantId);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Super Admin
      </Typography>

      {/* System Observability */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">System Health & Metrics</Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {(['30d', '90d', '1y', 'all'] as const).map((period) => (
                <Chip
                  key={period}
                  label={period === 'all' ? 'All' : period.toUpperCase()}
                  size="small"
                  onClick={() => setCostPeriod(period)}
                  color={costPeriod === period ? 'primary' : 'default'}
                  variant={costPeriod === period ? 'filled' : 'outlined'}
                />
              ))}
            </Box>
            <Typography variant="caption" color="text.secondary">
              Last updated: {healthUpdatedAt ? formatLastUpdate(healthUpdatedAt) : 'Loading...'}
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  System Status
                </Typography>
                {health ? (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip label={`API: ${health.checks.api.status}`} color={getStatusColor(health.checks.api.status)} size="small" />
                    <Chip label={`DB: ${health.checks.database.status}`} color={getStatusColor(health.checks.database.status)} size="small" />
                    <Chip label={`OpenAI: ${health.checks.openai.status}`} color={getStatusColor(health.checks.openai.status)} size="small" />
                    <Chip label={`Stripe: ${health.checks.stripe.status}`} color={getStatusColor(health.checks.stripe.status)} size="small" />
                  </Box>
                ) : (
                  <CircularProgress size={20} />
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 4, md: 2 }}>
            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  LLM Cost (Today)
                </Typography>
                <Typography variant="h6">${metrics?.llmCosts.today.toFixed(2) || '0.00'}</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 4, md: 2 }}>
            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  LLM Cost ({costPeriod === 'all' ? 'All Time' : costPeriod.toUpperCase()})
                </Typography>
                <Typography variant="h6">${metrics?.llmCosts.period?.toFixed(2) || '0.00'}</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 4, md: 2 }}>
            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  Errors ({costPeriod === 'all' ? 'All' : costPeriod.toUpperCase()})
                </Typography>
                <Typography variant="h6" color={metrics?.errors.today ? 'error' : 'inherit'}>
                  {metrics?.errors.today || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {links && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
            {Object.entries(links).map(([key, link]: [string, any]) => (
              <Button key={key} variant="outlined" size="small" href={link.url} target="_blank" rel="noopener noreferrer">
                {link.name}
              </Button>
            ))}
          </Box>
        )}
      </Box>

      {/* Tenants Table */}
      <Typography variant="h6" gutterBottom>
        Tenants
      </Typography>

      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={tenantsWithCosts || []}
          columns={columns}
          loading={isLoading}
          onRowClick={handleRowClick}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25 },
            },
          }}
          sx={{
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
            },
          }}
        />
      </Box>

      {/* Tenant Details Drawer */}
      <Drawer anchor="right" open={!!selectedTenantId} onClose={() => setSelectedTenantId(null)} sx={{ '& .MuiDrawer-paper': { width: 500 } }}>
        {selectedTenant && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5">{selectedTenant.name}</Typography>
              <IconButton onClick={() => setSelectedTenantId(null)} size="small">
                <X size={20} />
              </IconButton>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                {selectedTenant.workspace_slug}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {selectedTenant.workspace_email}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Created {new Date(selectedTenant.created_at).toLocaleDateString()}
              </Typography>
            </Box>

            {/* Cost Summary - Compact Cards */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                Cost Summary
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {(['30d', '90d', '1y', 'all'] as const).map((period) => (
                  <Chip
                    key={period}
                    label={period === 'all' ? 'All' : period.toUpperCase()}
                    size="small"
                    onClick={() => setCostPeriod(period)}
                    color={costPeriod === period ? 'primary' : 'default'}
                    variant={costPeriod === period ? 'filled' : 'outlined'}
                  />
                ))}
              </Box>
            </Box>
            <Grid container spacing={1.5} sx={{ mb: 3 }}>
              <Grid size={4}>
                <Card variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Runs
                  </Typography>
                  <Typography variant="h6">{selectedTenant.total_runs}</Typography>
                </Card>
              </Grid>
              <Grid size={4}>
                <Card variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Total
                  </Typography>
                  <Typography variant="h6">${Number(selectedTenant.total_cost).toFixed(2)}</Typography>
                </Card>
              </Grid>
              <Grid size={4}>
                <Card variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Avg
                  </Typography>
                  <Typography variant="h6">${Number(selectedTenant.avg_cost_per_run).toFixed(3)}</Typography>
                </Card>
              </Grid>
            </Grid>

            {/* Account Status */}
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
              Account
            </Typography>
            {tenantDetails ? (
              <Box sx={{ mb: 2, fontFamily: 'monospace', fontSize: '0.875rem' }}>
                <Box sx={{ display: 'flex', py: 0.5 }}>
                  <Typography sx={{ width: 180, color: 'text.secondary' }}>Status</Typography>
                  <Typography fontWeight="bold" sx={{ textTransform: 'capitalize' }}>
                    {tenantDetails.account_status}
                  </Typography>
                </Box>
                {tenantDetails.trial_ends_at && (
                  <Box sx={{ display: 'flex', py: 0.5 }}>
                    <Typography sx={{ width: 180, color: 'text.secondary' }}>Trial Ends</Typography>
                    <Typography fontWeight="bold">{new Date(tenantDetails.trial_ends_at).toLocaleDateString()}</Typography>
                  </Box>
                )}
                {tenantDetails.subscription_status && (
                  <Box sx={{ display: 'flex', py: 0.5 }}>
                    <Typography sx={{ width: 180, color: 'text.secondary' }}>Subscription</Typography>
                    <Typography fontWeight="bold" sx={{ textTransform: 'capitalize' }}>
                      {tenantDetails.subscription_status}
                    </Typography>
                  </Box>
                )}
                {tenantDetails.subscription_owner && (
                  <Box sx={{ display: 'flex', py: 0.5 }}>
                    <Typography sx={{ width: 180, color: 'text.secondary' }}>Owner</Typography>
                    <Typography fontWeight="bold">{tenantDetails.subscription_owner}</Typography>
                  </Box>
                )}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {/* Users */}
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
              Users
            </Typography>
            {tenantDetails ? (
              <Box sx={{ mb: 2, fontFamily: 'monospace', fontSize: '0.875rem' }}>
                <Box sx={{ display: 'flex', py: 0.5 }}>
                  <Typography sx={{ width: 180, color: 'text.secondary' }}>Total</Typography>
                  <Typography fontWeight="bold">{tenantDetails.total_users}</Typography>
                </Box>
                <Box sx={{ display: 'flex', py: 0.5 }}>
                  <Typography sx={{ width: 180, color: 'text.secondary' }}>Admins</Typography>
                  <Typography fontWeight="bold">{tenantDetails.admin_users}</Typography>
                </Box>
                <Box sx={{ display: 'flex', py: 0.5 }}>
                  <Typography sx={{ width: 180, color: 'text.secondary' }}>Portco</Typography>
                  <Typography fontWeight="bold">{tenantDetails.portco_users}</Typography>
                </Box>
                {tenantDetails.pending_users > 0 && (
                  <Box sx={{ display: 'flex', py: 0.5 }}>
                    <Typography sx={{ width: 180, color: 'text.secondary' }}>Pending Approval</Typography>
                    <Typography fontWeight="bold" color="warning.main">
                      {tenantDetails.pending_users}
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {/* Business Activity */}
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
              Activity (30d)
            </Typography>
            {tenantDetails ? (
              <Box sx={{ mb: 2, fontFamily: 'monospace', fontSize: '0.875rem' }}>
                <Box sx={{ display: 'flex', py: 0.5 }}>
                  <Typography sx={{ width: 180, color: 'text.secondary' }}>Funds</Typography>
                  <Typography fontWeight="bold">{tenantDetails.funds}</Typography>
                </Box>
                <Box sx={{ display: 'flex', py: 0.5 }}>
                  <Typography sx={{ width: 180, color: 'text.secondary' }}>Portfolio Companies</Typography>
                  <Typography fontWeight="bold">{tenantDetails.portfolio_companies}</Typography>
                </Box>
                <Box sx={{ display: 'flex', py: 0.5 }}>
                  <Typography sx={{ width: 180, color: 'text.secondary' }}>Submissions</Typography>
                  <Typography fontWeight="bold">{tenantDetails.submissions_30d}</Typography>
                </Box>
                <Box sx={{ display: 'flex', py: 0.5 }}>
                  <Typography sx={{ width: 180, color: 'text.secondary' }}>Metrics Tracked</Typography>
                  <Typography fontWeight="bold">{tenantDetails.metrics}</Typography>
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
          </Box>
        )}
      </Drawer>
    </Box>
  );
}
